import { Link } from "react-router-dom";

export interface ProjectCardData {
  id: string;
  tag: string;         // ribbon label (e.g. "Mobile flow")
  category: string;    // small label above title (e.g. "UI | UX")
  title: string;
  description: string;
  color: string;       // folder background color
  tagColor?: string;   // ribbon color, defaults to #1D3FE8 (blue)
  image?: string;      // optional right-panel image URL
  href?: string;
}

interface ProjectCardProps extends ProjectCardData {
  delay?: number;
}

/**
 * Floating folder card for case studies.
 * Shape: small tab above main rounded-rect body (folder analogy).
 * Left ribbon tag, right image panel, bottom-left white text.
 * Hover: floats up with deepened shadow.
 */
export function ProjectCard({
  tag,
  category,
  title,
  description,
  color,
  tagColor = "#1D3FE8",
  image,
  href,
  delay,
}: ProjectCardProps) {
  const inner = (
    <div
      className="relative pt-[18px] group z-[10000]"
      style={delay !== undefined ? { animationDelay: `${delay}ms` } : undefined}
    >
      {/* Folder tab — same color as body, sits above the card */}
      <div
        className="absolute top-0 left-6 w-[88px] h-[18px] rounded-t-[10px] rounded-br-none rounded-bl-none"
        style={{ backgroundColor: color }}
      />

      {/* Main folder body */}
      <div
        className="relative rounded-[20px] overflow-hidden
                   aspect-[3/2] min-h-[200px]
                   shadow-[0_6px_24px_rgba(0,0,0,0.13)]
                   transition-all duration-300 ease-out
                   group-hover:-translate-y-2
                   group-hover:shadow-[0_18px_48px_rgba(0,0,0,0.22)]"
        style={{ backgroundColor: color }}
      >
        {/* Ribbon tag — left side flag, hidden on mobile */}
        <div className="absolute left-0 top-10 z-10 hidden sm:flex items-center">
          <div
            className="pl-3 py-[9px] flex items-center"
            style={{
              backgroundColor: tagColor,
              paddingRight: "22px",
              clipPath:
                "polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%)",
            }}
          >
            <span className="font-[Bricolage_Grotesque] font-semibold text-white text-sm whitespace-nowrap leading-none">
              {tag}
            </span>
          </div>
        </div>

        {/* Image panel — top-right, clears bottom text area */}
        {image && (
          <div className="absolute right-0 top-4 w-[62%] h-[85%] rounded-l-[12px] overflow-hidden">
            <img
              src={image}
              alt={title}
              className="w-full h-full object-contain object-center"
            />
          </div>
        )}

        {/* Text block — bottom left, right edge must stay 16px clear of image */}
        <div
          className={`absolute bottom-5 left-5 text-white ${
            image ? "max-w-[33%]" : "max-w-[75%]"
          }`}
        >
          <p className="hidden sm:block font-[DM_Sans] text-[11px] uppercase tracking-widest mb-1.5 opacity-70">
            {category}
          </p>
          <h3 className="font-[Bricolage_Grotesque] font-normal text-[clamp(1rem,2vw,1.4rem)] leading-tight mb-2">
            {title}
          </h3>
          <p className="font-[DM_Sans] text-xs md:text-[13px] opacity-75 leading-snug">
            {description}
          </p>
        </div>
      </div>
    </div>
  );

  if (href) {
    return (
      <Link to={href} className="block no-underline cursor-pointer">
        {inner}
      </Link>
    );
  }
  return inner;
}
